<script setup lang="ts">
import { AlertTriangle, Info } from "@lucide/vue";
import { computed, inject, watch } from "vue";
import type {
  DiscountCouponAfterCopyAction,
  DiscountCouponBlock,
  DiscountCouponMode,
  DiscountCouponValueType,
} from "@aswin.dev/types";
import CheckboxItem from "./CheckboxItem.vue";
import FieldRow from "./FieldRow.vue";
import MergeTagInput from "../MergeTagInput.vue";
import NumberWithSuffix from "./NumberWithSuffix.vue";
import { useI18n } from "../../composables/useI18n";
import { inputClass, labelClass } from "../../constants/styleConstants";
import { EDITOR_KEY } from "../../keys";
import { dynamicDiscountModesLockedForCouponBlock } from "../../utils/discountCouponFlow";
import { hustlepopsHelpUrl } from "../../constants/hustlepopsHelp";

const props = defineProps<{
  block: DiscountCouponBlock;
}>();

const emit = defineEmits<{
  (e: "update", updates: Partial<DiscountCouponBlock>): void;
}>();

const { t } = useI18n();

const editor = inject(EDITOR_KEY, null);

function updateField<K extends keyof DiscountCouponBlock>(
  key: K,
  value: DiscountCouponBlock[K],
): void {
  emit("update", { [key]: value } as Partial<DiscountCouponBlock>);
}

const discountDynamicModesLocked = computed(() =>
  editor
    ? dynamicDiscountModesLockedForCouponBlock(
        editor.content.value,
        props.block.id,
      )
    : false,
);

function modeOptionDisabled(mode: DiscountCouponMode): boolean {
  if (!discountDynamicModesLocked.value) return false;
  return mode === "none" || mode === "auto_code";
}

function setMode(mode: DiscountCouponMode): void {
  if (mode !== "manual_shopify" && modeOptionDisabled(mode)) return;
  updateField("discountMode", mode);
}

watch(
  [discountDynamicModesLocked, () => props.block.discountMode],
  ([locked, mode]) => {
    if (!locked) return;
    if (mode === "none" || mode === "auto_code") {
      updateField("discountMode", "manual_shopify");
    }
  },
  { immediate: true },
);

function modeOptionAriaLabel(mode: DiscountCouponMode, label: string): string {
  if (!modeOptionDisabled(mode)) return label;
  return `${label}. ${t.discountCoupon.dynamicModesRequirePriorFormHint}`;
}

const manualInvalid = computed(
  () =>
    props.block.discountMode === "manual_shopify" &&
    props.block.manualDiscountCode.trim() === "",
);

const showValueRow = computed(
  () =>
    props.block.discountMode === "auto_code" &&
    props.block.valueType !== "free_shipping",
);

const showCurrencyRow = computed(
  () =>
    props.block.discountMode === "auto_code" &&
    props.block.valueType === "fixed_amount",
);

const valueSuffix = computed(() => {
  if (props.block.valueType === "percentage_off") return "%";
  return (props.block.currencyCode || "USD").toUpperCase();
});

function clampPercent(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.min(100, Math.max(0, n));
}

function clampPositive(n: number): number {
  if (!Number.isFinite(n) || n < 0) return 0;
  return n;
}

function onValueAmount(n: number): void {
  if (props.block.valueType === "percentage_off") {
    updateField("valueAmount", clampPercent(n));
  } else {
    updateField("valueAmount", clampPositive(n));
  }
}

function onExpirationDays(n: number): void {
  const v = Number.isFinite(n) ? Math.max(1, Math.floor(n)) : 1;
  updateField("expirationDays", v);
}

function onCurrencyInput(ev: Event): void {
  const raw = (ev.target as HTMLInputElement).value
    .toUpperCase()
    .replace(/[^A-Z]/g, "")
    .slice(0, 3);
  updateField("currencyCode", raw);
}

const valueTypeOptions: { value: DiscountCouponValueType; label: string }[] = [
  { value: "percentage_off", label: t.discountCoupon.types.percentageOff },
  { value: "fixed_amount", label: t.discountCoupon.types.fixedAmount },
  { value: "free_shipping", label: t.discountCoupon.types.freeShipping },
];

const modeRadios: { mode: DiscountCouponMode; label: string }[] = [
  { mode: "none", label: t.discountCoupon.modeNone },
  { mode: "auto_code", label: t.discountCoupon.modeAuto },
  { mode: "manual_shopify", label: t.discountCoupon.modeManual },
];

const showCopyBehavior = computed(() => props.block.discountMode !== "none");

const afterCopyOptions: {
  value: DiscountCouponAfterCopyAction;
  label: string;
}[] = [
  { value: "none", label: t.discountCoupon.afterClickNone },
  { value: "load_page", label: t.discountCoupon.afterClickLoadPage },
  { value: "close_popup", label: t.discountCoupon.afterClickCloseCampaign },
  { value: "next_step", label: t.discountCoupon.afterClickNextStep },
];
</script>

<template>
  <p
    class="tpl:m-0 tpl:mb-4 tpl:text-xs tpl:leading-normal tpl:text-[var(--tpl-text-muted)]"
  >
    {{ t.discountCoupon.description }}
  </p>

  <div
    v-if="discountDynamicModesLocked"
    class="tpl:mb-3 tpl:flex tpl:items-start tpl:gap-2 tpl:rounded tpl:border tpl:border-[color-mix(in_oklab,var(--tpl-primary)_35%,var(--tpl-border))] tpl:bg-[color-mix(in_oklab,var(--tpl-primary)_6%,var(--tpl-bg))] tpl:px-3 tpl:py-2 tpl:text-xs tpl:leading-snug tpl:text-[var(--tpl-text)]"
    role="status"
  >
    <Info
      class="tpl:mt-0.5 tpl:size-4 tpl:shrink-0 tpl:text-[var(--tpl-primary)]"
      :stroke-width="2"
      aria-hidden="true"
    />
    <span>{{ t.discountCoupon.dynamicModesRequirePriorFormHint }}</span>
  </div>

  <div
    class="tpl:mb-4 tpl:flex tpl:flex-col tpl:gap-3"
    role="radiogroup"
    :aria-label="t.blocks.discountCoupon"
  >
    <label
      v-for="opt in modeRadios"
      :key="opt.mode"
      :class="[
        'tpl:flex tpl:items-start tpl:gap-2 tpl:text-sm',
        modeOptionDisabled(opt.mode)
          ? 'tpl:cursor-not-allowed tpl:text-[var(--tpl-text-muted)] tpl:opacity-70'
          : 'tpl:cursor-pointer tpl:text-[var(--tpl-text)]',
      ]"
    >
      <input
        type="radio"
        class="tpl:mt-1 tpl:size-4 tpl:accent-[var(--tpl-primary)] disabled:tpl:cursor-not-allowed"
        :class="
          modeOptionDisabled(opt.mode)
            ? 'tpl:cursor-not-allowed'
            : 'tpl:cursor-pointer'
        "
        :checked="block.discountMode === opt.mode"
        :disabled="modeOptionDisabled(opt.mode)"
        :aria-label="modeOptionAriaLabel(opt.mode, opt.label)"
        @change="setMode(opt.mode)"
      />
      <span>{{ opt.label }}</span>
    </label>
  </div>

  <template v-if="block.discountMode === 'auto_code'">
    <p
      class="tpl:m-0 tpl:mb-3 tpl:text-xs tpl:leading-normal tpl:text-[var(--tpl-text-muted)]"
    >
      {{ t.discountCoupon.autoHint }}
    </p>

    <FieldRow :label="t.discountCoupon.selectType">
      <select
        :class="inputClass"
        :value="block.valueType"
        @change="
          updateField(
            'valueType',
            ($event.target as HTMLSelectElement)
              .value as DiscountCouponValueType,
          )
        "
      >
        <option v-for="o in valueTypeOptions" :key="o.value" :value="o.value">
          {{ o.label }}
        </option>
      </select>
    </FieldRow>

    <FieldRow v-if="showValueRow" :label="t.discountCoupon.value">
      <NumberWithSuffix
        :model-value="block.valueAmount"
        :min="0"
        :max="block.valueType === 'percentage_off' ? 100 : undefined"
        :suffix="valueSuffix"
        @update:model-value="onValueAmount($event)"
      />
    </FieldRow>

    <FieldRow v-if="showCurrencyRow" :label="t.discountCoupon.currencyCode">
      <input
        type="text"
        :class="inputClass"
        maxlength="3"
        autocapitalize="characters"
        autocomplete="off"
        spellcheck="false"
        placeholder="USD"
        :value="block.currencyCode"
        @input="onCurrencyInput"
      />
    </FieldRow>

    <div class="tpl:mb-3.5 tpl:flex tpl:flex-col tpl:gap-2">
      <CheckboxItem
        :model-value="block.expirationEnabled"
        :label="t.discountCoupon.expiration"
        @update:model-value="updateField('expirationEnabled', $event)"
      />
      <div v-if="block.expirationEnabled" class="tpl:pl-6">
        <NumberWithSuffix
          :model-value="block.expirationDays"
          :min="1"
          :suffix="t.discountCoupon.expirationDaysSuffix"
          @update:model-value="onExpirationDays($event)"
        />
      </div>
    </div>

    <!-- <div class="tpl:mb-4">
      <CheckboxItem
        :model-value="block.tagCustomerWithCode"
        :label="t.discountCoupon.tagCustomer"
        @update:model-value="updateField('tagCustomerWithCode', $event)"
      />
    </div> -->

    <div class="tpl:mb-1">
      <span :class="labelClass">{{ t.discountCoupon.combinations }}</span>
      <div class="tpl:mt-2 tpl:flex tpl:flex-col tpl:gap-2">
        <CheckboxItem
          :model-value="block.combineProductDiscounts"
          :label="t.discountCoupon.combineProduct"
          @update:model-value="updateField('combineProductDiscounts', $event)"
        />
        <CheckboxItem
          :model-value="block.combineOrderDiscounts"
          :label="t.discountCoupon.combineOrder"
          @update:model-value="updateField('combineOrderDiscounts', $event)"
        />
        <CheckboxItem
          :model-value="block.combineShippingDiscounts"
          :label="t.discountCoupon.combineShipping"
          @update:model-value="updateField('combineShippingDiscounts', $event)"
        />
      </div>
    </div>
  </template>

  <template v-else-if="block.discountMode === 'manual_shopify'">
    <FieldRow :label="t.discountCoupon.manualPlaceholder">
      <input
        type="text"
        :class="[
          inputClass,
          manualInvalid
            ? 'tpl:border-[var(--tpl-danger)] tpl:bg-[color-mix(in_oklab,var(--tpl-danger)_8%,var(--tpl-bg))]'
            : '',
        ]"
        :value="block.manualDiscountCode"
        autocomplete="off"
        spellcheck="false"
        :aria-invalid="manualInvalid"
        @input="
          updateField(
            'manualDiscountCode',
            ($event.target as HTMLInputElement).value,
          )
        "
      />
      <div
        v-if="manualInvalid"
        class="tpl:mt-2 tpl:flex tpl:items-center tpl:gap-1.5 tpl:text-xs tpl:text-[var(--tpl-danger)]"
      >
        <AlertTriangle class="tpl:size-3.5 tpl:shrink-0" :stroke-width="2" />
        <span>{{ t.discountCoupon.manualBlankError }}</span>
      </div>
    </FieldRow>
    <p
      class="tpl:m-0 tpl:mt-2 tpl:text-xs tpl:leading-normal tpl:text-[var(--tpl-text-muted)]"
    >
      <a
        :href="hustlepopsHelpUrl.discountCoupon"
        target="_blank"
        rel="noopener noreferrer"
        class="tpl:text-[var(--tpl-primary)] tpl:underline tpl:underline-offset-2"
        >{{ t.discountCoupon.manualFooterLead }}</a
      >{{ t.discountCoupon.manualFooterTail }}
    </p>
  </template>

  <template v-if="showCopyBehavior">
    <div class="tpl:mt-6 tpl:border-t tpl:border-[var(--tpl-border)] tpl:pt-5">
      <label
        :class="[
          labelClass,
          'tpl:!mb-2 tpl:!text-[10px] tpl:!font-semibold tpl:!uppercase tpl:!tracking-wide tpl:!text-[var(--tpl-text-muted)]',
        ]"
      >
        {{ t.discountCoupon.confirmationTextLabel }}
      </label>
      <input
        type="text"
        :class="inputClass"
        :value="block.copyConfirmationText"
        autocomplete="off"
        spellcheck="true"
        @input="
          updateField(
            'copyConfirmationText',
            ($event.target as HTMLInputElement).value,
          )
        "
      />

      <div
        class="tpl:mt-4 tpl:flex tpl:flex-col tpl:gap-2"
        role="radiogroup"
        :aria-label="t.discountCoupon.afterClickLabel"
      >
        <span
          :class="[
            labelClass,
            'tpl:!mb-0 tpl:!text-[10px] tpl:!font-semibold tpl:!uppercase tpl:!tracking-wide tpl:!text-[var(--tpl-text-muted)]',
          ]"
        >
          {{ t.discountCoupon.afterClickLabel }}
        </span>
        <label
          v-for="opt in afterCopyOptions"
          :key="opt.value"
          class="tpl:flex tpl:cursor-pointer tpl:items-start tpl:gap-2 tpl:text-sm tpl:text-[var(--tpl-text)]"
        >
          <input
            type="radio"
            class="tpl:mt-1 tpl:size-4 tpl:cursor-pointer tpl:accent-[var(--tpl-primary)]"
            :checked="block.afterCopyAction === opt.value"
            @change="updateField('afterCopyAction', opt.value)"
          />
          <span>{{ opt.label }}</span>
        </label>
      </div>

      <div v-if="block.afterCopyAction === 'load_page'" class="tpl:mt-3">
        <label :class="labelClass">{{
          t.discountCoupon.afterClickPageUrl
        }}</label>
        <MergeTagInput
          :model-value="block.afterCopyUrl ?? ''"
          type="url"
          :placeholder="t.discountCoupon.afterClickUrlPlaceholder"
          @update:model-value="updateField('afterCopyUrl', $event)"
        />
      </div>
    </div>
  </template>
</template>
